import React from 'react'
import { apiFetch } from './api-client'

// Mirrors GET /api/v1/fraflytning/syn-status (backend/auth/routers/fraflytning.py).
// Any date may be null; booleans are null when not computable.
export interface SynStatus {
  contract_id: string
  // false for erhverv: lejeloven §187 does not apply -> render nothing syn-related.
  applies: boolean
  move_in: {
    deadline: string | null
    held_date: string | null
    missed: boolean | null
    flagged: boolean | null
  }
  move_out: {
    fraflytning_dato: string | null
    syn_deadline: string | null
    syn_indkaldt_dato: string | null
    syn_afholdt_dato: string | null
    rapport_leveret_dato: string | null
    notice_ok: boolean | null
    syn_within_deadline: boolean | null
    rapport_within_deadline: boolean | null
  }
  landlord_multi_unit: boolean | null
  istandsaettelse_allowed: boolean | null
  issues: string[]
  rules: {
    move_in_deadline_days: number
    move_out_deadline_days: number
    notice_minimum_days: number
    report_max_days_after_syn: number
  }
}

/** Fetches syn-status for a contract. Errors (incl. 404) are swallowed: status = null. */
export function useSynStatus(contractId: string | null | undefined): SynStatus | null {
  const [status, setStatus] = React.useState<SynStatus | null>(null)
  React.useEffect(() => {
    if (!contractId) return
    let alive = true
    apiFetch<SynStatus>(`/api/v1/fraflytning/syn-status?contract_id=${encodeURIComponent(contractId)}`)
      .then(s => { if (alive) setStatus(s) })
      .catch(() => { /* quiet: panel/warning simply not shown */ })
    return () => { alive = false }
  }, [contractId])
  // Ignore a stale result left over from a previously selected contract.
  return contractId && status?.contract_id === contractId ? status : null
}

/**
 * Warn when the daily job found the move-in syn missed, or flagged it and no timely
 * report exists (a report registered in time after the flag clears the contradiction).
 */
export function showMoveInWarning(mi: SynStatus['move_in']): boolean {
  if (mi.missed) return true
  const timely = !!(mi.held_date && mi.deadline && mi.held_date <= mi.deadline)
  return !!mi.flagged && !timely
}
